/**
 * api/designSystem.js — Design System do projeto (Onda 1 do plano de qualidade).
 *
 * GET    /projects/{id}/design-system                      → DS ativo (ou null / 404 = sem DS)
 * POST   /projects/{id}/design-system                      → cria RASCUNHO (pré-visualização)
 * PUT    /projects/{id}/design-system/{dsId}/activate      → ativa o rascunho
 * DELETE /projects/{id}/design-system/{dsId}               → remove (soft-delete)
 * POST   /projects/{id}/design-system/extract              → RASCUNHO extraído das telas (Onda 5)
 * POST   /projects/{id}/design-system/retematizar          → aplica o DS ativo às telas (Onda 5)
 *
 * Formato do DesignSystemOut: {id, projectId, version, origin, mode, sourceKind,
 * status, canonical, compactCss, avisos, contraste, createdAt}.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client';

export const designSystemKey = (projectId) => ['design-system', projectId];

/** useDesignSystem — DS ativo do projeto; `null` quando não há (404 ou corpo nulo). */
export function useDesignSystem(projectId) {
  const api = useApi();
  return useQuery({
    queryKey: designSystemKey(projectId),
    queryFn:  async () => {
      try {
        const ds = await api.get(`/projects/${projectId}/design-system`);
        return ds ?? null;
      } catch (err) {
        if (err?.status === 404) return null;
        throw err;
      }
    },
    enabled:  Boolean(projectId),
  });
}

/**
 * usePreviewDesignSystem — envia o conteúdo (JSON W3C / CSS) e recebe o RASCUNHO
 * normalizado, com avisos e contraste. body: { content, sourceKind, mode }.
 */
export function usePreviewDesignSystem(projectId) {
  const api = useApi();
  return useMutation({
    mutationFn: (body) => api.post(`/projects/${projectId}/design-system`, body),
  });
}

/** useActivateDesignSystem — ativa o rascunho `dsId` (o ativo anterior vira superseded). */
export function useActivateDesignSystem(projectId) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dsId) => api.put(`/projects/${projectId}/design-system/${dsId}/activate`, {}),
    onSuccess:  () => qc.invalidateQueries({ queryKey: designSystemKey(projectId) }),
  });
}

/** useDeleteDesignSystem — remove o DS `dsId` (soft-delete). */
export function useDeleteDesignSystem(projectId) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dsId) => api.delete(`/projects/${projectId}/design-system/${dsId}`),
    onSuccess:  () => qc.invalidateQueries({ queryKey: designSystemKey(projectId) }),
  });
}

/**
 * useExtractDesignSystem — extrai tokens do HTML das telas atuais do Design Studio e
 * devolve o RASCUNHO (origin "extracted"). body: { mode }.
 */
export function useExtractDesignSystem(projectId) {
  const api = useApi();
  return useMutation({
    mutationFn: (body) => api.post(`/projects/${projectId}/design-system/extract`, body),
  });
}

/**
 * useRetematizar — aplica o DS ATIVO às telas atuais sem regerá-las.
 * Devolve { telas, semVariaveis, dsVersion }. Invalida o DS e as telas do Design Studio.
 */
export function useRetematizar(projectId) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`/projects/${projectId}/design-system/retematizar`, {}),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: designSystemKey(projectId) });
      qc.invalidateQueries({ queryKey: ['design-studio', projectId] });
    },
  });
}
